/**
 * Council Form Requirements Database
 *
 * Defines field requirements for major UK councils' liability transfer portals.
 * Used to show "Required" badges on mandatory fields in the submission helper.
 *
 * Coverage: 15 major councils with verified portal requirements
 * Last Updated: 2026-01-25
 */

export interface CouncilFormRequirement {
  authorityId: string;
  authorityName: string;
  requiredFields: Array<'pcn_number' | 'vrm' | 'hirer_name' | 'hirer_address'>;
  optionalFields: Array<'pcn_number' | 'vrm' | 'hirer_name' | 'hirer_address'>;
  notes?: string;
  lastVerified: string;
}

export const COUNCIL_FORM_REQUIREMENTS: CouncilFormRequirement[] = [
  // London Councils
  {
    authorityId: 'westminster',
    authorityName: 'Westminster City Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name', 'hirer_address'],
    optionalFields: [],
    notes: 'Westminster requires all fields to be completed',
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'camden',
    authorityName: 'Camden Council',
    requiredFields: ['pcn_number', 'vrm'],
    optionalFields: ['hirer_name', 'hirer_address'],
    notes: 'Hirer details can be included in representation text',
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'kensington_chelsea',
    authorityName: 'Kensington and Chelsea Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name', 'hirer_address'],
    optionalFields: [],
    notes: 'All fields mandatory for liability transfer',
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'lambeth',
    authorityName: 'Lambeth Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name'],
    optionalFields: ['hirer_address'],
    notes: 'Address can be provided in supporting documents',
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'wandsworth',
    authorityName: 'Wandsworth Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name', 'hirer_address'],
    optionalFields: [],
    notes: 'Complete hirer information required',
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'tower_hamlets',
    authorityName: 'Tower Hamlets Council',
    requiredFields: ['pcn_number', 'vrm'],
    optionalFields: ['hirer_name', 'hirer_address'],
    notes: 'Minimal requirements - hirer details in documents',
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'hackney',
    authorityName: 'Hackney Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name'],
    optionalFields: ['hirer_address'],
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'islington',
    authorityName: 'Islington Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name', 'hirer_address'],
    optionalFields: [],
    notes: 'Strict requirement for all fields',
    lastVerified: '2026-01-25'
  },

  // Major UK Cities
  {
    authorityId: 'birmingham',
    authorityName: 'Birmingham City Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name'],
    optionalFields: ['hirer_address'],
    notes: 'Address optional for online submissions',
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'manchester',
    authorityName: 'Manchester City Council',
    requiredFields: ['pcn_number', 'vrm'],
    optionalFields: ['hirer_name', 'hirer_address'],
    notes: 'Hirer details can be in attached documents',
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'bristol',
    authorityName: 'Bristol City Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name', 'hirer_address'],
    optionalFields: [],
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'leeds',
    authorityName: 'Leeds City Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name'],
    optionalFields: ['hirer_address'],
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'liverpool',
    authorityName: 'Liverpool City Council',
    requiredFields: ['pcn_number', 'vrm'],
    optionalFields: ['hirer_name', 'hirer_address'],
    notes: 'Flexible requirements - hirer info in representation',
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'edinburgh',
    authorityName: 'City of Edinburgh Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name', 'hirer_address'],
    optionalFields: [],
    notes: 'Scottish councils require full hirer details',
    lastVerified: '2026-01-25'
  },
  {
    authorityId: 'glasgow',
    authorityName: 'Glasgow City Council',
    requiredFields: ['pcn_number', 'vrm', 'hirer_name', 'hirer_address'],
    optionalFields: [],
    notes: 'Complete information required for all transfers',
    lastVerified: '2026-01-25'
  },
];

/**
 * Get form requirements for a specific authority
 * Returns undefined if authority not in database (fallback to showing all fields as optional)
 */
export function getCouncilRequirements(authorityName: string): CouncilFormRequirement | undefined {
  const normalized = authorityName.toLowerCase().trim();

  return COUNCIL_FORM_REQUIREMENTS.find(req =>
    req.authorityName.toLowerCase().includes(normalized) ||
    normalized.includes(req.authorityId)
  );
}

/**
 * Check if a specific field is required for an authority
 */
export function isFieldRequired(
  authorityName: string,
  fieldName: 'pcn_number' | 'vrm' | 'hirer_name' | 'hirer_address'
): boolean {
  const requirements = getCouncilRequirements(authorityName);
  if (!requirements) return false; // Unknown council = treat as optional

  return requirements.requiredFields.includes(fieldName);
}
